import { ComponentProps, HTMLInputTypeAttribute, useEffect, useRef } from 'react';
import classNames from 'classnames';
import type Typography from '@alltrails/core/components/Typography';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import { IconRendererProps } from '../IconRenderer/IconRenderer';
import InputField from '../InputField';
import { InputBaseProps, Size } from '../../types';
import styles from './styles/styles.module.scss';
import getErrorMessage from './getErrorMessage';

type TextInputProps = InputBaseProps & {
  autoComplete?: string; // https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
  autoFocus?: boolean;
  characterLimit?: number;
  className?: string;
  inputMode?: 'decimal' | 'email' | 'numeric' | 'tel' | 'text';
  isFocused?: boolean;
  labelTextColor?: ComponentProps<typeof Typography>['color'];
  leadingIcon?: IconDefinition<'orientation'>;
  name?: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  recurlyDataId?: string;
  required?: boolean;
  testId?: string;
  trailingIcon?: IconDefinition<'orientation'>;
  type?: HTMLInputTypeAttribute;
  value?: string;
  size?: Size<'sm' | 'md'>;
};

const TextInput = ({
  autoComplete,
  autoFocus,
  className,
  disabled,
  errorText,
  helperText,
  hideLabel,
  inputMode,
  labelText,
  labelTextColor,
  labelTextVariant,
  leadingIcon,
  name,
  onChange,
  onKeyDown,
  placeholder,
  recurlyDataId,
  required = false,
  testId,
  trailingIcon,
  type = 'text',
  value,
  isFocused,
  characterLimit,
  size = 'md'
}: TextInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const iconDefaults: IconRendererProps['defaults'] = { color: disabled ? '--color-text-disabled' : '--color-text-secondary', size: 'md' };

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFocused]);

  const errorMessage = getErrorMessage(errorText, value, required, characterLimit);

  return (
    <InputField
      className={className}
      disabled={disabled}
      errorText={errorMessage}
      helperText={helperText}
      labelText={hideLabel ? '' : labelText}
      labelTextColor={labelTextColor}
      labelTextVariant={labelTextVariant}
      characterLimitText={value && characterLimit ? `${value.length} / ${characterLimit}` : undefined}
      size={size}
    >
      <div className={classNames(styles.inputContainer, styles[size], { [styles.disabled]: disabled, [styles.hasError]: Boolean(errorMessage) })}>
        {leadingIcon ? <IconRenderer icon={leadingIcon} defaults={iconDefaults} /> : null}
        <input
          ref={inputRef}
          aria-label={hideLabel ? labelText : undefined}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={classNames(styles.input, styles[size], { [styles.hasLeadingIcon]: !!leadingIcon, [styles.hasTrailingIcon]: !!trailingIcon })}
          disabled={disabled}
          inputMode={inputMode}
          name={name}
          onChange={e => {
            onChange(e.target.value);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          type={type}
          value={value}
          data-testid={testId}
          data-recurly={recurlyDataId}
        />
        {trailingIcon ? <IconRenderer icon={trailingIcon} defaults={iconDefaults} /> : null}
      </div>
    </InputField>
  );
};

export default TextInput;
